import { useEffect, useState } from "react";
import { InvoiceForm } from "../../components/forms";
import { PageHeader } from "../../components/layout";
import { AutoSearch, Dropdown } from "../../components/ui";
import { useCustomerSearch } from "../../hooks/customer-address";
import { Address, CustomerAddress, Invoice, WorkOrder } from "../../models";
import { useCustomer } from "../../hooks/customer";
import { APPLICATION_SETTING } from "../../globals/enums";
import { useAppSettingsContext } from "../../contexts";
import { useAddWorkOrder } from "../../hooks/work-order";
import { useAddInvoice } from "../../hooks/invoice";
import { useNavigate } from "react-router-dom";
import { InvoiceTotalFooter } from "../invoices/components/InvoiceTotalFooter";

export const OverTheCounterSalePage = () => {
  const { getSetting } = useAppSettingsContext();
  const ON_OVER_THE_COUNTER_SALE = getSetting(
    APPLICATION_SETTING.ON_OVER_THE_COUNTER_SALE,
  )?.settingId;
  const BILLING_ADDRESS_TYPE = getSetting(
    APPLICATION_SETTING.BILLING_ADDRESS_TYPE,
  )?.settingId;
  const OVER_THE_COUNTER_INDICATOR = getSetting(
    APPLICATION_SETTING.OVER_THE_COUNTER_INDICATOR,
  )?.settingId;
  const [workOrder, setWorkOrder] = useState<WorkOrder>({
    callDate: new Date(),
    jobDescription: "OVER THE COUNTER SALE",
    siteAddressId: "",
    billingAddressId: "",
    workOrderStateId: ON_OVER_THE_COUNTER_SALE,
    jobTypeIds: [OVER_THE_COUNTER_INDICATOR],
    isOtc: true,
  } as WorkOrder);

  const [invoice, setInvoice] = useState<any>();
  const [billingAddresses, setBillingAddresses] = useState<Address[]>([]);
  const [selectedBillingAddress, setSelectedBillingAddress] =
    useState<Address | null>(null);
  const { data: customer } = useCustomer(workOrder.customerId);
  const { mutateAsync: createWorkOrder } = useAddWorkOrder();
  const { mutateAsync: createInvoice } = useAddInvoice();
  const navigate = useNavigate();

  useEffect(() => {
    if (!customer) {
      setBillingAddresses([]);
      setSelectedBillingAddress(null);
      setWorkOrder((current) => ({
        ...current,
        billingAddressId: "",
        siteAddressId: "",
      }));
      return;
    }
    const addresses = customer.addresses ?? [];
    const customerBillingAddresses = BILLING_ADDRESS_TYPE
      ? addresses.filter(
          (address) =>
            address.addressType?.id === BILLING_ADDRESS_TYPE ||
            address.addressTypeId === BILLING_ADDRESS_TYPE,
        )
      : addresses;
    const billingAddress = customerBillingAddresses[0];
    setBillingAddresses(customerBillingAddresses);
    if (!billingAddress) {
      setSelectedBillingAddress(null);
      setWorkOrder((current) => ({
        ...current,
        billingAddressId: "",
        siteAddressId: "",
      }));
      return;
    }
    setSelectedBillingAddress(billingAddress);
    setWorkOrder((current) => ({
      ...current,
      billingAddressId: billingAddress.id,
      siteAddressId: billingAddress.id,
    }));
  }, [customer, BILLING_ADDRESS_TYPE]);

  const save = async (invoice: Invoice, navigateToPreview?: boolean) => {
    const newWorkOrder = await createWorkOrder(workOrder as WorkOrder);
    const newInvoice = await createInvoice({
      ...invoice,
      workOrderId: newWorkOrder.id,
    } as Invoice);
    navigateToPreview && navigate(`/invoices/receipt/${newInvoice.id}`);
  };

  return (
    <>
      <PageHeader title="Over The Counter Sale" />
      <AutoSearch
        label="Search Customer By Name, Address, or P.O ..."
        keys={["customerNumber", "fullName", "email", "phone", "address", "po"]}
        onOptionSelected={(customer: CustomerAddress | null) => {
          if (!customer) {
            setWorkOrder((current) => ({
              ...current,
              customerId: "",
              billingAddressId: "",
              siteAddressId: "",
            }));
            return;
          }
          setWorkOrder((current) => ({
            ...current,
            customerId: customer.customerId,
          }));
        }}
        searchFunction={useCustomerSearch}
        separator="|"
        sx={{ width: 700, mt: 2, mb: 2 }}
      />
      {customer && (
        <Dropdown
          label="Billing Address"
          data={billingAddresses}
          keys={[
            "streetNumber",
            "streetName",
            "unit",
            "city",
            "province",
            "postalCode",
            "note",
          ]}
          loading={false}
          value={selectedBillingAddress}
          onOptionSelected={(address) => {
            setSelectedBillingAddress(address);
            setWorkOrder((current) => ({
              ...current,
              billingAddressId: address?.id ?? "",
              siteAddressId: address?.id ?? "",
            }));
          }}
          sx={{ width: 700, mb: 2 }}
        />
      )}
      {customer && (
        <InvoiceForm
          customer={customer}
          mode="OVER_THE_COUNTER"
          onSubmitted={(invoice, navigateToPreview) =>
            invoice && save(invoice, navigateToPreview)
          }
          onInvoiceChanged={(entity) => setInvoice(entity)}
        />
      )}
      {invoice && <InvoiceTotalFooter invoice={invoice} />}
    </>
  );
};
